import CreatorStatusBadge from '@/components/creator/CreatorStatusBadge';
import CreatorAvatar from '@/components/creators/CreatorAvatar';
import PaginationControls from '@/components/shared/PaginationControls';
import TableEmptyState from '@/components/table/TableEmptyState';
import TableSkeleton from '@/components/table/TableSkeleton';
import {
  TableScrollArea,
  stickyColumnProps,
  stickyEndColumnProps,
} from '@/components/table/TableScrollArea';
import { Tooltip } from '@/components/ui/tooltip';
import { platformOf } from '@/components/contact/platform-of';
import { usePrefetchCreatorProfileImages } from '@/hooks/queries/use-creator-profile-image-query';
import { useOpenExistingCreatorSurface } from '@/hooks/use-open-existing-creator-surface';
import { useSearchCreatorsQuery } from '@/hooks/queries/use-search-creators-query';
import { ButtonGroup, IconButton, Table, Text, VStack } from '@chakra-ui/react';
import type { SearchCreator } from '@fluenceur/types';

import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Filters from './Creators.Filter';
import { Eye, MessageText, User } from 'iconoir-react';

const creatorQueryParsers = {
  creatorSearch: parseAsString.withDefault(''),
  creatorPlatform: parseAsString,
  creatorStatus: parseAsString,
  creatorPage: parseAsInteger.withDefault(1),
};

const CreatorsTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openExistingCreatorSurface = useOpenExistingCreatorSurface();

  // Read URL state for filters - using namespaced parameters
  const [
    { creatorSearch: search, creatorPlatform: platform, creatorStatus: status, creatorPage: page },
    setCreatorQuery,
  ] = useQueryStates(creatorQueryParsers);
  // Build filters for the query
  const filters = {
    search: search || undefined,
    platform: platform || undefined,
    status: status || undefined,
    page,
  };

  // Track if we've ever loaded data to avoid skeleton on filter changes
  const hasLoadedDataRef = useRef(false);

  // Use the search creators query directly
  const { data, isFetching } = useSearchCreatorsQuery(filters);

  const results = data?.data || [];
  const paginationMeta = data?.meta;

  useEffect(() => {
    if (!paginationMeta || isFetching) return;

    const lastAvailablePage = Math.max(paginationMeta.lastPage, 1);
    if (page > lastAvailablePage) {
      setCreatorQuery({ creatorPage: lastAvailablePage });
    }
  }, [isFetching, page, paginationMeta, setCreatorQuery]);

  // Prefetch avatars for all creators in a single batch request
  usePrefetchCreatorProfileImages(results.map((c) => c.username));

  // Mark as loaded once we get any data
  if (data && !hasLoadedDataRef.current) {
    hasLoadedDataRef.current = true;
  }

  // Show skeleton when there is no data for the active URL state yet.
  const initialLoading = isFetching && (!hasLoadedDataRef.current || !data);

  const handleView = (creator: SearchCreator) => {
    openExistingCreatorSurface(
      { id: creator.id, username: creator.username },
      { platform: platformOf(creator.platform) }
    );
  };

  const handleDM = (creator: SearchCreator) => {
    if (creator.conversationId) {
      navigate(`/messages/${creator.conversationId}`);
    }
  };

  const renderEmptyState = () => (
    <TableEmptyState
      colSpan={6}
      icon={<User width={22} height={22} />}
      title={t('creators.table.empty.title')}
      description={t('creators.table.empty.description')}
    />
  );

  const renderCreatorRow = (creator: SearchCreator) => (
    <Table.Row key={creator.id}>
      <Table.Cell {...stickyColumnProps('300px')}>
        <CreatorAvatar
          creatorId={creator.username}
          creatorName={creator.name}
          fallbackSrc={creator.profileImage}
          username={creator.username}
          profileUrl={creator.profileUrl}
          showDetails
          size="sm"
        />
      </Table.Cell>
      <Table.Cell>
        <Text>{creator.platform}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text>{creator.followers || t('creators.table.noFollowers')}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text>{creator.mentionsCount}</Text>
        <Text fontSize="xs" color="gray.fg">
          {creator.lastMentionDate
            ? t('creators.table.lastMentionDate', { date: creator.lastMentionDate })
            : t('creators.table.noFollowers')}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <CreatorStatusBadge
          status={creator.status}
          statusText={t(`creators.status.${creator.status}`)}
        />
      </Table.Cell>
      <Table.Cell width="120px" textAlign="end" {...stickyEndColumnProps('120px')}>
        <ButtonGroup gap={1} justify="end" size="sm" variant="ghost" colorPalette="gray">
          <IconButton onClick={() => handleView(creator)} aria-label={t('creators.actions.view')}>
            <Eye width={20} height={20} />
          </IconButton>
          <Tooltip
            content={t('creators.actions.noConversation')}
            disabled={!!creator.conversationId}
          >
            <IconButton
              onClick={() => handleDM(creator)}
              disabled={!creator.conversationId}
              aria-label={t('creators.actions.message')}
            >
              <MessageText width={20} height={20} />
            </IconButton>
          </Tooltip>
        </ButtonGroup>
      </Table.Cell>
    </Table.Row>
  );

  return (
    <VStack gap={6} w="full" align="flex-start">
      <Filters />
      {initialLoading ? (
        <TableSkeleton // TODO: Create Creators.TableSkeleton.tsx
          rows={5}
          columns={[
            { width: '300px' },
            { width: '250px' },
            { width: '120px' },
            { width: '120px' },
            { width: '100px' },
            { width: '120px' },
          ]}
          stickyColumns={1}
          stickyLastColumn
          firstColumnWidth="300px"
          lastColumnWidth="120px"
        />
      ) : (
        <TableScrollArea>
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader width="300px" {...stickyColumnProps('300px')}>
                  {t('creators.table.columns.creator')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="250px">
                  {t('creators.table.columns.platform')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="120px">
                  {t('creators.table.columns.followers')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="120px">
                  {t('creators.table.columns.mentions')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="100px">
                  {t('creators.table.columns.status')}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  width="120px"
                  textAlign="end"
                  aria-label={t('creators.table.columns.actions')}
                  {...stickyEndColumnProps('120px')}
                />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {!results.length ? renderEmptyState() : results.map(renderCreatorRow)}
            </Table.Body>
          </Table.Root>
        </TableScrollArea>
      )}
      {paginationMeta && paginationMeta.lastPage > 1 && (
        <PaginationControls
          pagination={{
            total: paginationMeta.total,
            perPage: paginationMeta.perPage,
            currentPage: paginationMeta.currentPage,
            lastPage: paginationMeta.lastPage,
            from: (paginationMeta.currentPage - 1) * paginationMeta.perPage + 1,
            to: Math.min(paginationMeta.currentPage * paginationMeta.perPage, paginationMeta.total),
          }}
          onPageChange={(newPage) => setCreatorQuery({ creatorPage: newPage })}
          isLoading={isFetching}
        />
      )}
    </VStack>
  );
};

export default CreatorsTable;
